/**
 * The `.dm_68` netfield tables.
 * Ported from Quake III Arena's code/qcommon/msg.c.
 *
 * Copyright (C) 1999-2005 Id Software, Inc.
 * Copyright (C) 2026 Overbounce contributors
 * Licensed under the GNU General Public License v2 or later. See LICENSE.
 *
 * GENERATED by `tools/gen-netfields.py` from `refs/quake3/qcommon/msg.c`.
 * Do not edit by hand -- re-run the script.
 *
 * **The order of these tables IS the wire format.** A delta message says how
 * many fields changed (`lc`) and then writes exactly that many change bits in
 * this order; move one line and every demo decodes into plausible nonsense
 * with no error raised anywhere. That is why they are extracted mechanically
 * rather than transcribed, and why the generator asserts the counts (51 and
 * 48) it found.
 *
 * `bits: 0` does NOT mean "no bits". It means the field is a float, sent
 * either as zero, as a 13-bit biased integer when it happens to be integral,
 * or as a raw 32-bit pattern. `bits < 0` means a signed integer of that
 * width.
 */

/** One `netField_t`. `slot` is the index into the state's word array. */
export interface NetField {
  name: string;
  slot: number;
  bits: number;
}

export const ENTITY_FIELDS: readonly NetField[] = [
  { name: 'pos.trTime', slot: 1, bits: 32 },
  { name: 'pos.trBase[0]', slot: 2, bits: 0 },
  { name: 'pos.trBase[1]', slot: 3, bits: 0 },
  { name: 'pos.trDelta[0]', slot: 4, bits: 0 },
  { name: 'pos.trDelta[1]', slot: 5, bits: 0 },
  { name: 'pos.trBase[2]', slot: 6, bits: 0 },
  { name: 'apos.trBase[1]', slot: 7, bits: 0 },
  { name: 'pos.trDelta[2]', slot: 8, bits: 0 },
  { name: 'apos.trBase[0]', slot: 9, bits: 0 },
  { name: 'event', slot: 10, bits: 10 },
  { name: 'angles2[1]', slot: 11, bits: 0 },
  { name: 'eType', slot: 12, bits: 8 },
  { name: 'torsoAnim', slot: 13, bits: 8 },
  { name: 'eventParm', slot: 14, bits: 8 },
  { name: 'legsAnim', slot: 15, bits: 8 },
  { name: 'groundEntityNum', slot: 16, bits: 10 },
  { name: 'pos.trType', slot: 17, bits: 8 },
  { name: 'eFlags', slot: 18, bits: 19 },
  { name: 'otherEntityNum', slot: 19, bits: 10 },
  { name: 'weapon', slot: 20, bits: 8 },
  { name: 'clientNum', slot: 21, bits: 8 },
  { name: 'angles[1]', slot: 22, bits: 0 },
  { name: 'pos.trDuration', slot: 23, bits: 32 },
  { name: 'apos.trType', slot: 24, bits: 8 },
  { name: 'origin[0]', slot: 25, bits: 0 },
  { name: 'origin[1]', slot: 26, bits: 0 },
  { name: 'origin[2]', slot: 27, bits: 0 },
  { name: 'solid', slot: 28, bits: 24 },
  { name: 'powerups', slot: 29, bits: 16 },
  { name: 'modelindex', slot: 30, bits: 8 },
  { name: 'otherEntityNum2', slot: 31, bits: 10 },
  { name: 'loopSound', slot: 32, bits: 8 },
  { name: 'generic1', slot: 33, bits: 8 },
  { name: 'origin2[2]', slot: 34, bits: 0 },
  { name: 'origin2[0]', slot: 35, bits: 0 },
  { name: 'origin2[1]', slot: 36, bits: 0 },
  { name: 'modelindex2', slot: 37, bits: 8 },
  { name: 'angles[0]', slot: 38, bits: 0 },
  { name: 'time', slot: 39, bits: 32 },
  { name: 'apos.trTime', slot: 40, bits: 32 },
  { name: 'apos.trDuration', slot: 41, bits: 32 },
  { name: 'apos.trBase[2]', slot: 42, bits: 0 },
  { name: 'apos.trDelta[0]', slot: 43, bits: 0 },
  { name: 'apos.trDelta[1]', slot: 44, bits: 0 },
  { name: 'apos.trDelta[2]', slot: 45, bits: 0 },
  { name: 'time2', slot: 46, bits: 32 },
  { name: 'angles[2]', slot: 47, bits: 0 },
  { name: 'angles2[0]', slot: 48, bits: 0 },
  { name: 'angles2[2]', slot: 49, bits: 0 },
  { name: 'constantLight', slot: 50, bits: 32 },
  { name: 'frame', slot: 51, bits: 16 },
];

/** Slot count for one `entityState_t`: `number` plus the 51 net fields. */
export const ENTITY_SLOTS = 52;

/**
 * Where each `entityState_t` member lives in the word array.
 *
 * Slot 0 is `number`, which is NOT a net field -- it rides the packet
 * entity header instead. The rest follow the net table order, which is an
 * arbitrary but stable choice: only the name-to-slot mapping matters here,
 * where in `ENTITY_FIELDS` the ORDER is the wire format itself.
 */
export const ES = {
  number: 0,
  pos_trTime: 1,
  pos_trBase_0: 2,
  pos_trBase_1: 3,
  pos_trDelta_0: 4,
  pos_trDelta_1: 5,
  pos_trBase_2: 6,
  apos_trBase_1: 7,
  pos_trDelta_2: 8,
  apos_trBase_0: 9,
  event: 10,
  angles2_1: 11,
  eType: 12,
  torsoAnim: 13,
  eventParm: 14,
  legsAnim: 15,
  groundEntityNum: 16,
  pos_trType: 17,
  eFlags: 18,
  otherEntityNum: 19,
  weapon: 20,
  clientNum: 21,
  angles_1: 22,
  pos_trDuration: 23,
  apos_trType: 24,
  origin_0: 25,
  origin_1: 26,
  origin_2: 27,
  solid: 28,
  powerups: 29,
  modelindex: 30,
  otherEntityNum2: 31,
  loopSound: 32,
  generic1: 33,
  origin2_2: 34,
  origin2_0: 35,
  origin2_1: 36,
  modelindex2: 37,
  angles_0: 38,
  time: 39,
  apos_trTime: 40,
  apos_trDuration: 41,
  apos_trBase_2: 42,
  apos_trDelta_0: 43,
  apos_trDelta_1: 44,
  apos_trDelta_2: 45,
  time2: 46,
  angles_2: 47,
  angles2_0: 48,
  angles2_2: 49,
  constantLight: 50,
  frame: 51,
} as const;

export const PLAYER_FIELDS: readonly NetField[] = [
  { name: 'commandTime', slot: 0, bits: 32 },
  { name: 'origin[0]', slot: 1, bits: 0 },
  { name: 'origin[1]', slot: 2, bits: 0 },
  { name: 'bobCycle', slot: 3, bits: 8 },
  { name: 'velocity[0]', slot: 4, bits: 0 },
  { name: 'velocity[1]', slot: 5, bits: 0 },
  { name: 'viewangles[1]', slot: 6, bits: 0 },
  { name: 'viewangles[0]', slot: 7, bits: 0 },
  { name: 'weaponTime', slot: 8, bits: -16 },
  { name: 'origin[2]', slot: 9, bits: 0 },
  { name: 'velocity[2]', slot: 10, bits: 0 },
  { name: 'legsTimer', slot: 11, bits: 8 },
  { name: 'pm_time', slot: 12, bits: -16 },
  { name: 'eventSequence', slot: 13, bits: 16 },
  { name: 'torsoAnim', slot: 14, bits: 8 },
  { name: 'movementDir', slot: 15, bits: 4 },
  { name: 'events[0]', slot: 16, bits: 8 },
  { name: 'legsAnim', slot: 17, bits: 8 },
  { name: 'events[1]', slot: 18, bits: 8 },
  { name: 'pm_flags', slot: 19, bits: 16 },
  { name: 'groundEntityNum', slot: 20, bits: 10 },
  { name: 'weaponstate', slot: 21, bits: 4 },
  { name: 'eFlags', slot: 22, bits: 16 },
  { name: 'externalEvent', slot: 23, bits: 10 },
  { name: 'gravity', slot: 24, bits: 16 },
  { name: 'speed', slot: 25, bits: 16 },
  { name: 'delta_angles[1]', slot: 26, bits: 16 },
  { name: 'externalEventParm', slot: 27, bits: 8 },
  { name: 'viewheight', slot: 28, bits: -8 },
  { name: 'damageEvent', slot: 29, bits: 8 },
  { name: 'damageYaw', slot: 30, bits: 8 },
  { name: 'damagePitch', slot: 31, bits: 8 },
  { name: 'damageCount', slot: 32, bits: 8 },
  { name: 'generic1', slot: 33, bits: 8 },
  { name: 'pm_type', slot: 34, bits: 8 },
  { name: 'delta_angles[0]', slot: 35, bits: 16 },
  { name: 'delta_angles[2]', slot: 36, bits: 16 },
  { name: 'torsoTimer', slot: 37, bits: 12 },
  { name: 'eventParms[0]', slot: 38, bits: 8 },
  { name: 'eventParms[1]', slot: 39, bits: 8 },
  { name: 'clientNum', slot: 40, bits: 8 },
  { name: 'weapon', slot: 41, bits: 5 },
  { name: 'viewangles[2]', slot: 42, bits: 0 },
  { name: 'grapplePoint[0]', slot: 43, bits: 0 },
  { name: 'grapplePoint[1]', slot: 44, bits: 0 },
  { name: 'grapplePoint[2]', slot: 45, bits: 0 },
  { name: 'jumppad_ent', slot: 46, bits: 10 },
  { name: 'loopSound', slot: 47, bits: 16 },
];

/** Slot count for one `playerState_t`. The stats/persistant/ammo/powerups
 *  arrays are NOT in here -- they are delta-coded separately. */
export const PLAYER_SLOTS = 48;

/** Where each `playerState_t` net member lives in the word array. */
export const PS = {
  commandTime: 0,
  origin_0: 1,
  origin_1: 2,
  bobCycle: 3,
  velocity_0: 4,
  velocity_1: 5,
  viewangles_1: 6,
  viewangles_0: 7,
  weaponTime: 8,
  origin_2: 9,
  velocity_2: 10,
  legsTimer: 11,
  pm_time: 12,
  eventSequence: 13,
  torsoAnim: 14,
  movementDir: 15,
  events_0: 16,
  legsAnim: 17,
  events_1: 18,
  pm_flags: 19,
  groundEntityNum: 20,
  weaponstate: 21,
  eFlags: 22,
  externalEvent: 23,
  gravity: 24,
  speed: 25,
  delta_angles_1: 26,
  externalEventParm: 27,
  viewheight: 28,
  damageEvent: 29,
  damageYaw: 30,
  damagePitch: 31,
  damageCount: 32,
  generic1: 33,
  pm_type: 34,
  delta_angles_0: 35,
  delta_angles_2: 36,
  torsoTimer: 37,
  eventParms_0: 38,
  eventParms_1: 39,
  clientNum: 40,
  weapon: 41,
  viewangles_2: 42,
  grapplePoint_0: 43,
  grapplePoint_1: 44,
  grapplePoint_2: 45,
  jumppad_ent: 46,
  loopSound: 47,
} as const;
